"use client";
import React, { useRef, useState } from "react";
import { AiChatPanel } from "./AiChatPanel";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChatbotFABProps {
  compact?: boolean;
}

export function ChatbotFAB({ compact = false }: ChatbotFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  const openPanel = () => {
    setHasOpened(true);
    setIsOpen(true);
  };

  const closePanel = () => {
    setIsOpen(false);
    setIsExpanded(false);
    window.setTimeout(() => openButtonRef.current?.focus(), 0);
  };

  return (
    <>
      {/* チャットパネル */}
      {hasOpened && (
        <aside
          hidden={!isOpen}
          aria-label="AIアシスタント"
          data-expanded={isExpanded}
          className={cn(
            "bg-background fixed z-50 overflow-hidden shadow-2xl",
            "transition-[inset,width,height,border-radius] duration-300 ease-out motion-reduce:transition-none",
            isExpanded
              ? "inset-0 h-dvh w-full rounded-none md:left-auto md:w-[480px] md:border-l xl:w-[520px]"
              : "inset-x-3 bottom-[max(1rem,env(safe-area-inset-bottom))] h-[min(640px,calc(100dvh-2rem))] rounded-2xl border sm:inset-x-auto sm:bottom-6 sm:right-6 sm:h-[min(640px,calc(100dvh-3rem))] sm:w-[380px]",
            !isExpanded &&
              compact &&
              "bottom-auto left-auto right-3 top-16 sm:right-6"
          )}
        >
          <AiChatPanel
            isExpanded={isExpanded}
            onToggleExpanded={() => setIsExpanded((current) => !current)}
            onNavigate={() => setIsExpanded(false)}
            onClose={closePanel}
          />
        </aside>
      )}

      {/* フローティングボタン */}
      {!isOpen && (
        <button
          ref={openButtonRef}
          type="button"
          onClick={openPanel}
          className={cn(
            "fixed z-50",
            "flex items-center justify-center rounded-full shadow-lg",
            "transition-[transform,box-shadow] duration-200 motion-reduce:transition-none",
            "bg-primary text-primary-foreground hover:scale-105 hover:shadow-xl active:scale-95 motion-reduce:hover:scale-100 motion-reduce:active:scale-100",
            compact
              ? "right-4 top-3 size-9"
              : "bottom-[max(1rem,env(safe-area-inset-bottom))] right-0 size-11 sm:bottom-6 sm:right-6 sm:size-14"
          )}
          aria-label="AIアシスタントを開く"
        >
          <Sparkles
            aria-hidden="true"
            className={compact ? "size-4" : "size-5 sm:size-6"}
          />
        </button>
      )}
    </>
  );
}
